/**
 * Phone Sorting Utility
 *
 * This module handles intelligent sorting of phone models with special rules:
 * - iPhone Air appears first (priority 9999)
 * - iPhone SE appears last (negative priority)
 * - Galaxy: S series before Z Flip before Z Fold
 * - Galaxy variants: FE before base before plus before ultra
 * - Brand order is randomized per session but model ordering within brands is consistent
 */

/**
 * Creates a randomized brand order for priority brands
 * Uses Fisher-Yates shuffle with session-based seed
 */
export function createRandomizedBrandOrder() {
  const priorityBrands = ['Apple', 'Google', 'Samsung'];
  const shuffled = [...priorityBrands];

  // Simple Fisher-Yates shuffle with session-based seed
  const seed = Date.now() % 1000; // Use current session time as seed
  let currentIndex = shuffled.length;

  while (currentIndex !== 0) {
    const randomIndex = (seed + currentIndex) % currentIndex;
    currentIndex--;
    [shuffled[currentIndex], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[currentIndex]];
  }

  return shuffled;
}

/**
 * Extracts model information from phone name for sorting
 * @param {string} phone - Phone model name
 * @returns {Object} Model information including type, number, variant, and sort order
 */
function extractModelInfo(phone) {
  // Handle iPhone models
  if (phone.toLowerCase().includes('iphone')) {
    // Special case: iPhone SE should appear last (lowest priority)
    if (phone.toLowerCase().includes('se')) {
      const seMatch = phone.match(/iphone\s*se.*?(\d+).*?generation/i);
      const generation = seMatch ? parseInt(seMatch[1]) : 1;
      return {
        type: 'iphone',
        number: -generation, // Negative numbers to put SE at the end, newer generations first
        variant: 'se',
        variantOrder: 0
      };
    }

    // Special case: iPhone Air is standalone (highest priority, no number)
    if (phone.toLowerCase().includes('air') && !phone.match(/iphone\s*\d+/i)) {
      return {
        type: 'iphone',
        number: 9999, // Give it highest number to appear first
        variant: 'air',
        variantOrder: 0 // Highest priority within its "series"
      };
    }

    const match = phone.match(/iphone\s*(\d+)\s*(plus|pro\s*max|pro|mini)?/i);
    if (match) {
      const number = parseInt(match[1]);
      const variant = match[2] ? match[2].toLowerCase().replace(/\s+/g, '') : 'base';

      let variantOrder = 1; // base model
      if (variant === 'plus') variantOrder = 2;
      else if (variant === 'pro') variantOrder = 3;
      else if (variant === 'promax') variantOrder = 4;
      else if (variant === 'mini') variantOrder = 5;

      return { type: 'iphone', number, variant, variantOrder };
    }
  }

  // Handle Pixel models
  if (phone.toLowerCase().includes('pixel')) {
    const match = phone.match(/pixel\s*(\d+)\s*(pro\s*xl|pro|xl|a)?/i);
    if (match) {
      const number = parseInt(match[1]);
      const variant = match[2] ? match[2].toLowerCase().replace(/\s+/g, '') : 'base';

      let variantOrder = 1; // base model
      if (variant === 'a') variantOrder = 2;
      else if (variant === 'xl') variantOrder = 3;
      else if (variant === 'pro') variantOrder = 4;
      else if (variant === 'proxl') variantOrder = 5;

      return { type: 'pixel', number, variant, variantOrder };
    }
  }

  // Handle Galaxy models
  if (phone.toLowerCase().includes('galaxy')) {
    // Handle S series (e.g., S25, S25 Ultra, S24, S24+, etc.)
    const sMatch = phone.match(/galaxy\s*s(\d+)\s*(plus|\+|ultra|fe)?/i);
    if (sMatch) {
      const number = parseInt(sMatch[1]);
      const variant = sMatch[2] ? sMatch[2].toLowerCase().replace(/\+/, 'plus') : 'base';

      let variantOrder = 1; // FE first
      if (variant === 'fe') variantOrder = 1;
      else if (variant === 'base') variantOrder = 2;
      else if (variant === 'plus') variantOrder = 3;
      else if (variant === 'ultra') variantOrder = 4;

      return { type: 'galaxy', series: 's', number, variant, variantOrder };
    }

    // Handle Z Flip series (e.g., Z Flip7, Z Flip6, etc.)
    const zFlipMatch = phone.match(/galaxy\s*z\s*flip(\d+)/i);
    if (zFlipMatch) {
      const number = parseInt(zFlipMatch[1]);
      return { type: 'galaxy', series: 'zflip', number, variant: 'base', variantOrder: 1 };
    }

    // Handle Z Fold series (e.g., Z Fold7, Z Fold6, etc.)
    const zFoldMatch = phone.match(/galaxy\s*z\s*fold(\d+)/i);
    if (zFoldMatch) {
      const number = parseInt(zFoldMatch[1]);
      return { type: 'galaxy', series: 'zfold', number, variant: 'base', variantOrder: 1 };
    }
  }

  // Handle Moto models
  if (phone.toLowerCase().includes('moto')) {
    const match = phone.match(/moto\s*(\w+)\s*(\d+)\s*(plus|\+|power|edge)?/i);
    if (match) {
      const series = match[1].toLowerCase();
      const number = parseInt(match[2]) || 0;
      const variant = match[3] ? match[3].toLowerCase().replace(/\+/, 'plus') : 'base';

      let variantOrder = 1; // base model
      if (variant === 'plus') variantOrder = 2;
      else if (variant === 'power') variantOrder = 3;
      else if (variant === 'edge') variantOrder = 4;

      return { type: 'moto', series, number, variant, variantOrder };
    }
  }

  // Fallback for unknown patterns
  return { type: 'unknown', text: phone };
}

/**
 * Sorts phone models intelligently within the same brand
 * @param {string} phoneA - First phone model name
 * @param {string} phoneB - Second phone model name
 * @returns {number} Sort comparison result
 */
export function sortPhoneModels(phoneA, phoneB) {
  const infoA = extractModelInfo(phoneA);
  const infoB = extractModelInfo(phoneB);

  // If different types or unknown, fall back to alphabetical
  if (infoA.type !== infoB.type || infoA.type === 'unknown') {
    return phoneB.localeCompare(phoneA); // Z to A for non-smart sorting
  }

  // For Galaxy phones, sort by series first (S before Z)
  if (infoA.type === 'galaxy' && infoB.type === 'galaxy') {
    if (infoA.series !== infoB.series) {
      // Define series priority: s > zflip > zfold
      const seriesOrder = { 's': 1, 'zflip': 2, 'zfold': 3 };
      return (seriesOrder[infoA.series] || 99) - (seriesOrder[infoB.series] || 99);
    }
  }

  // Same type (and series for Galaxy), sort by number (higher first), then by variant
  if (infoA.number !== infoB.number) {
    return infoB.number - infoA.number; // Higher numbers first
  }

  // Same number, sort by variant order
  return infoA.variantOrder - infoB.variantOrder;
}

/**
 * Creates a phone comparison function with randomized brand ordering
 * @param {string[]} randomizedBrandOrder - Array of brand names in randomized order
 * @param {Function} getBrandName - Function to extract brand name from phone
 * @returns {Function} Comparison function for sorting phones
 */
export function createPhoneSortComparator(randomizedBrandOrder, getBrandName) {
  return (phoneA, phoneB) => {
    const brandA = getBrandName(phoneA);
    const brandB = getBrandName(phoneB);

    const isPriorityA = randomizedBrandOrder.includes(brandA);
    const isPriorityB = randomizedBrandOrder.includes(brandB);

    // Both are priority brands
    if (isPriorityA && isPriorityB) {
      if (brandA !== brandB) {
        // Different priority brands - use randomized order
        const indexA = randomizedBrandOrder.indexOf(brandA);
        const indexB = randomizedBrandOrder.indexOf(brandB);
        return indexA - indexB;
      } else {
        // Same brand - use smart model sorting
        return sortPhoneModels(phoneA, phoneB);
      }
    }

    // One is priority, one is not
    if (isPriorityA && !isPriorityB) return -1;
    if (!isPriorityA && isPriorityB) return 1;

    // Both are non-priority brands - alphabetical A to Z
    return phoneA.localeCompare(phoneB);
  };
}
